import { angleLerp, lerp, type PlayerSnap } from '@kokun/shared';

interface Sample {
  t: number;
  s: PlayerSnap;
}

/**
 * Búfer de snapshots de un jugador remoto. Se renderiza en el pasado (serverNow − retardo)
 * interpolando entre las dos muestras que rodean ese instante; si faltan datos, se extrapola
 * como máximo 120 ms para evitar saltos sin «inventar» recorridos largos.
 */
export class InterpolationBuffer {
  private samples: Sample[] = [];

  push(t: number, s: PlayerSnap) {
    const last = this.samples[this.samples.length - 1];
    if (last && t <= last.t) return;
    this.samples.push({ t, s });
    if (this.samples.length > 30) this.samples.shift();
  }

  get latest(): PlayerSnap | null {
    return this.samples[this.samples.length - 1]?.s ?? null;
  }

  sample(renderT: number): { x: number; y: number; z: number; yaw: number; snap: PlayerSnap } | null {
    const n = this.samples.length;
    if (!n) return null;
    if (renderT <= this.samples[0].t || n === 1) {
      const s = this.samples[0].s;
      return { x: s.p[0], y: s.p[1], z: s.p[2], yaw: s.yaw, snap: s };
    }
    for (let i = n - 1; i > 0; i--) {
      const a = this.samples[i - 1];
      const b = this.samples[i];
      if (renderT >= a.t && renderT <= b.t) {
        const k = (renderT - a.t) / Math.max(1, b.t - a.t);
        // Un salto grande (teletransporte/reaparición) no se interpola
        if (Math.hypot(b.s.p[0] - a.s.p[0], b.s.p[2] - a.s.p[2]) > 6) return { x: b.s.p[0], y: b.s.p[1], z: b.s.p[2], yaw: b.s.yaw, snap: b.s };
        return {
          x: lerp(a.s.p[0], b.s.p[0], k),
          y: lerp(a.s.p[1], b.s.p[1], k),
          z: lerp(a.s.p[2], b.s.p[2], k),
          yaw: angleLerp(a.s.yaw, b.s.yaw, k),
          snap: k < 0.5 ? a.s : b.s,
        };
      }
    }
    // Extrapolación limitada
    const a = this.samples[n - 2];
    const b = this.samples[n - 1];
    const ahead = Math.min(120, renderT - b.t);
    const k = ahead / Math.max(1, b.t - a.t);
    return {
      x: b.s.p[0] + (b.s.p[0] - a.s.p[0]) * k,
      y: b.s.p[1] + (b.s.p[1] - a.s.p[1]) * k,
      z: b.s.p[2] + (b.s.p[2] - a.s.p[2]) * k,
      yaw: b.s.yaw,
      snap: b.s,
    };
  }
}
